import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiService } from "../services/api";
import { ReactComponent as Logo } from "../logo.svg";
import InlineFeedback from "./InlineFeedback";
import { useFeedback } from "../hooks/useFeedback";
import {
  BeakerIcon,
  SparklesIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowsUpDownIcon,
  EnvelopeIcon,
  XMarkIcon,
  ArrowRightEndOnRectangleIcon,
} from "@heroicons/react/24/outline";

interface OnboardingProps {
  onComplete: () => void;
}

const Onboarding: React.FC<OnboardingProps> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [contactType, setContactType] = useState<"analyzer_plus" | "converter">("converter");
  const [contactForm, setContactForm] = useState({ email: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const contactFeedback = useFeedback(0);

  const handleGetStarted = () => {
    setCurrentPage(2);
  };

  const handleAnalyzerClick = async () => {
    setLoading(true);
    setError("");

    try {
      const instanceName = "My Instance";

      // Get referral source from localStorage if available
      const refSource = localStorage.getItem('referral_source') || undefined;

      // Auto-register the instance
      await apiService.register(instanceName, undefined, refSource);
      localStorage.removeItem('referral_source');
      onComplete();
      navigate("/");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to set up your instance. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    contactFeedback.clear();

    try {
      await apiService.submitContactRequest({
        request_type: contactType,
        ...contactForm,
      });
      setContactSubmitted(true);
      setContactForm({ email: "", note: "" });
    } catch (error) {
      contactFeedback.showError("Failed to send request. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const openContactPopup = (type: "analyzer_plus" | "converter") => {
    setContactType(type);
    setContactSubmitted(false);
    contactFeedback.clear();
    setShowContactPopup(true);
  };

  const closeContactPopup = () => {
    setShowContactPopup(false);
    setContactForm({ email: "", note: "" });
    setContactSubmitted(false);
    contactFeedback.clear();
  };

  return (
    <div className="min-h-screen bg-body-bg flex flex-col">
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        {/* Sign in for returning users */}
        <div className="flex justify-end -mt-6 mb-2">
          <Link to="/signin" className="btn-subtle">
            <ArrowRightEndOnRectangleIcon className="w-5 h-5" />
            Sign in
          </Link>
        </div>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <Logo className="h-16 w-auto" style={{ color: "#F8F9FA" }} />
          </div>
          <p className="text-xl text-body-color max-w-3xl mx-auto">
            Static code analysis and conversion. Modernize your data platform.
          </p>
        </div>

        {/* Main Content */}
        {currentPage === 1 ? (
          // Page 1: What is Alchemist
          <div className="max-w-4xl mx-auto mb-12">
            <div className="bg-widget-bg rounded-lg shadow-xl p-8 border border-secondary/20">
              <div className="flex items-center mb-4">
                <BeakerIcon className="h-8 w-8 text-primary mr-3" />
                <h2 className="text-2xl font-bold text-heading">What is Alchemist?</h2>
              </div>
              <p className="text-body-color mb-6">
                Alchemist analyzes and converts legacy code. It helps you modernize your data platform:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-success mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-body-color">Gives a high-level view of your code transformation.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-success mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-body-color">Understands data and code dependencies.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-success mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-body-color">Deterministic AST analysis and rule-based conversion, not find-and-replace. An optional LLM fallback for unsupported constructs, off by default.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-success mt-0.5 mr-2 flex-shrink-0" />
                  <span className="text-body-color">Converts legacy data environments to modern stacks.</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          // Page 2: Feature Cards
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Analyzer Card */}
            <div className="bg-widget-bg rounded-lg shadow-xl p-6 border border-secondary/20 flex flex-col">
              <div className="flex items-center mb-4">
                <ChartBarIcon className="h-8 w-8 text-primary mr-3" />
                <h3 className="text-xl font-bold text-heading">Analyzer</h3>
              </div>
              <p className="text-body-color mb-6 flex-grow">
                Analyzer profiles your SAS workloads. It measures code volume and complexity. It shows whether automated conversion is a good fit.
              </p>
              <button
                onClick={handleAnalyzerClick}
                disabled={loading}
                className="btn-primary-full w-full py-3 text-base"
              >
                {loading ? "Initializing..." : "Access Analyzer"}
              </button>
            </div>

            {/* Analyzer Plus Card */}
            <div className="bg-widget-bg rounded-lg shadow-xl p-6 border border-secondary/20 flex flex-col opacity-75">
              <div className="flex items-center mb-4">
                <SparklesIcon className="h-8 w-8 text-primary mr-3" />
                <h3 className="text-xl font-bold text-heading">Analyzer Plus</h3>
              </div>
              <p className="text-body-color mb-6 flex-grow">
                Analyzer Plus exports a full data set from your SAS source code. It includes lineage dependencies and procedure-level and statement-level details.
              </p>
              <button
                onClick={() => openContactPopup("analyzer_plus")}
                className="btn-secondary w-full py-3 text-base flex items-center justify-center"
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                Contact Us
              </button>
            </div>

            {/* Converter Card */}
            <div className="bg-widget-bg rounded-lg shadow-xl p-6 border border-secondary/20 flex flex-col opacity-75">
              <div className="flex items-center mb-4">
                <ArrowsUpDownIcon className="h-8 w-8 text-primary mr-3" />
                <h3 className="text-xl font-bold text-heading">Converter</h3>
              </div>
              <p className="text-body-color mb-6 flex-grow">
                Converter converts legacy SAS code to modern targets such as Databricks PySpark notebooks.
              </p>
              <button
                onClick={() => openContactPopup("converter")}
                className="btn-secondary w-full py-3 text-base flex items-center justify-center"
              >
                <EnvelopeIcon className="h-5 w-5 mr-2" />
                Contact Us
              </button>
            </div>
          </div>
        )}

        {/* Call to Action / Navigation */}
        <div className="text-center">
          {currentPage === 1 && (
            <button
              onClick={handleGetStarted}
              className="btn-primary-full inline-flex items-center px-8 py-4 text-lg"
            >
              Get Started
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </button>
          )}

          {error && (
            <div className="mt-4 max-w-md mx-auto">
              <div className="rounded-md bg-warning/10 border border-warning/30 p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-warning flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm text-warning">{error}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="w-full py-6 text-center">
        <p className="text-sm text-body-color">
          Made by{" "}
          <a
            href="https://t1a.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-hover transition-colors"
            style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 800 }}
          >
            T1A
          </a>
        </p>
      </div>

      {/* Contact Us Popup */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div className="bg-widget-bg rounded-lg p-8 max-w-md w-full mx-4 border border-secondary shadow-xl">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-heading">{contactType === "analyzer_plus" ? "Get Analyzer Plus" : "Get Converter"}</h2>
              <button
                onClick={closeContactPopup}
                className="text-body-color hover:text-heading transition-colors p-1 hover:bg-body-bg/50 rounded"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <p className="text-body-color mb-6">
              {contactType === "analyzer_plus"
                ? "Analyzer Plus gives detailed data sets from your source code. Use them to plan your migration."
                : "The Alchemist Converter is an enterprise feature. It converts your code automatically."}
            </p>
            {contactSubmitted ? (
              <div className="space-y-4">
                <InlineFeedback
                  feedback={{ kind: "success", message: "We got your request. We will contact you soon." }}
                />
                <button onClick={closeContactPopup} className="w-full btn-primary-full">
                  Done
                </button>
              </div>
            ) : (
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm text-secondary mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label htmlFor="note" className="block text-sm text-secondary mb-2">
                  Message
                </label>
                <textarea
                  id="note"
                  rows={4}
                  required
                  value={contactForm.note}
                  onChange={(e) => setContactForm({ ...contactForm, note: e.target.value })}
                  className="w-full px-3 py-3 bg-body-bg border border-secondary rounded-md text-body-color placeholder-secondary/50 focus:outline-none focus:border-primary transition-colors resize-none"
                  placeholder={
                    contactType === "analyzer_plus"
                      ? "Tell us about your migration needs..."
                      : "Tell us about your project and requirements..."
                  }
                />
              </div>
              <InlineFeedback feedback={contactFeedback.feedback} />
              <div className="flex gap-4">
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 btn-primary-full"
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
                <button
                  type="button"
                  onClick={closeContactPopup}
                  className="btn-secondary py-3"
                >
                  Cancel
                </button>
              </div>
            </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
